import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useMobileNav } from "@/contexts/MobileNavContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, User, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { PatientPrescriptionSummary } from "@/components/prescription/PatientPrescriptionSummary";
import { PrescriptionSuggestions } from "@/components/prescription/PrescriptionSuggestions";
import { MultimodalCapture } from "@/components/prescription/MultimodalCapture";
import { PrescriptionTable } from "@/components/prescription/PrescriptionTable";
import { PrescriptionStepsNav } from "@/components/prescription/PrescriptionStepsNav";
import { PrescriptionPatientLinker } from "@/components/prescription/PrescriptionPatientLinker";
import { PrescriptionFinalizationStep } from "@/components/prescription/PrescriptionFinalizationStep";
import { format } from "date-fns";

interface PrescriptionItem {
  id?: string;
  item_date?: string;
  item_type: string;
  name: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
  usage_route?: string;
  concentration?: string;
  observations?: string;
  acquisition?: string;
}

export default function PrescriptionCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setShowMobileNav } = useMobileNav();

  const [currentStep, setCurrentStep] = useState(1);
  const [currentPatientId, setCurrentPatientId] = useState<string | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [suggestedPatientInfo, setSuggestedPatientInfo] = useState<{
    name?: string;
    cpf?: string;
    confidence?: number;
  }>({});
  const [prescriptionItems, setPrescriptionItems] = useState<PrescriptionItem[]>([]);
  const [administrationPlan, setAdministrationPlan] = useState<any>(null);
  const [prescriptionId, setPrescriptionId] = useState<string | null>(null);
  const [showManualEntry, setShowManualEntry] = useState(false);

  // Ocultar o menu mobile ao montar e restaurar ao desmontar
  useEffect(() => {
    setShowMobileNav(false);
    return () => setShowMobileNav(true);
  }, [setShowMobileNav]);

  const { data: profile } = useQuery({
    queryKey: ["user-profile"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const savePrescriptionMutation = useMutation({
    mutationFn: async (status: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Create prescription
      const { data: prescription, error: prescError } = await supabase
        .from("prescriptions")
        .insert({
          professional_id: user.id,
          patient_id: selectedPatient.id,
          status,
          patient_snapshot: {
            full_name: selectedPatient.full_name,
            data_nascimento: selectedPatient.data_nascimento,
          },
        })
        .select()
        .single();

      if (prescError) throw prescError;

      // Create prescription items
      const itemsToInsert = prescriptionItems.map((item) => ({
        prescription_id: prescription.id,
        item_type: item.item_type,
        prescription: item.name,
        dosage: item.dosage,
        frequency: item.frequency,
        duration: item.duration,
        item_date: item.item_date,
      }));

      const { error: itemsError } = await supabase
        .from("prescription_items")
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;

      setPrescriptionId(prescription.id);
      return prescription;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prescriptions"] });
      toast.success("Prescrição salva com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao salvar prescrição");
    },
  });

  const generatePlanMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("generate-administration-plan", {
        body: {
          items: prescriptionItems,
          patientName: selectedPatient.full_name,
          patientAge: selectedPatient.data_nascimento
            ? Math.floor(
              (new Date().getTime() - new Date(selectedPatient.data_nascimento).getTime()) /
              (365.25 * 24 * 60 * 60 * 1000)
            )
            : null,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setAdministrationPlan(data);
      toast.success("Plano de administração gerado!");
    },
    onError: (error: any) => {
      if (error.message?.includes("429")) {
        toast.error("Limite de requisições excedido");
      } else if (error.message?.includes("402")) {
        toast.error("Créditos insuficientes");
      } else {
        toast.error("Erro ao gerar plano");
      }
    },
  });

  const handleCaptureComplete = async (result: any) => {
    console.log("🔍 handleCaptureComplete - Resultado recebido:", result);

    // Extract patient info if available
    if (result.patient_info) {
      const patientInfo = {
        name: result.patient_info.name,
        cpf: result.patient_info.cpf,
        confidence: result.patient_info.confidence || 0,
      };

      console.log("👤 Informações do paciente extraídas:", patientInfo);
      setSuggestedPatientInfo(patientInfo);

      // BUSCA AUTOMÁTICA DO PACIENTE - EXECUTAR IMEDIATAMENTE
      if (patientInfo.name || patientInfo.cpf) {
        console.log("🔎 Iniciando busca automática de paciente...");

        try {
          let query = supabase.from("patients_onboarding").select("*");

          if (patientInfo.cpf) {
            const cleanCpf = patientInfo.cpf.replace(/\D/g, "");
            console.log("🔎 Buscando por CPF:", cleanCpf);
            query = query.eq("cpf", cleanCpf);
          } else if (patientInfo.name) {
            console.log("🔎 Buscando por nome:", patientInfo.name);
            query = query.ilike("full_name", `%${patientInfo.name}%`);
          }

          const { data: patients, error } = await query.limit(1);

          console.log("📊 Resultado da busca:", { patients, error });

          if (!error && patients && patients.length > 0) {
            console.log("✅ Paciente encontrado:", patients[0]);
            setCurrentPatientId(patients[0].id);
            setSelectedPatient(patients[0]);
            toast.success(`Paciente "${patients[0].full_name}" vinculado automaticamente!`);
          } else {
            console.log("⚠️ Paciente não encontrado no banco. CPF:", patientInfo.cpf, "Nome:", patientInfo.name);
          }
        } catch (err) {
          console.error("❌ Erro ao buscar paciente automaticamente:", err);
        }
      }
    }

    // Extract prescription items
    if (result.items && Array.isArray(result.items)) {
      const formattedItems = result.items.map((item: any) => ({
        item_type: item.item_type || item.type || "medication",
        name: item.name || item.medication_name || item.drug_name || "",
        dosage: item.dosage,
        frequency: item.frequency,
        duration: item.duration,
        usage_route: item.usage_route,
        concentration: item.concentration,
        observations: item.observations,
        acquisition: item.acquisition,
        item_date: format(new Date(), "yyyy-MM-dd"),
      }));

      setPrescriptionItems([...prescriptionItems, ...formattedItems]);

      // Auto-navigate to Receita if items were extracted
      if (formattedItems.length > 0) {
        console.log(`✅ ${formattedItems.length} item(ns) extraído(s), navegando para Step 2`);
        setCurrentStep(2);
      } else if (!result.patient_info) {
        // Nenhum item E nenhum paciente identificado
        console.log("⚠️ Nenhum item ou paciente identificado na entrada");
      }
    }
  };

  const handlePatientLinked = async (patientId: string) => {
    setCurrentPatientId(patientId);

    // Fetch patient data
    const { data: patient, error } = await supabase
      .from("patients_onboarding")
      .select("*")
      .eq("id", patientId)
      .single();

    if (!error && patient) {
      setSelectedPatient(patient);
      toast.success("Paciente vinculado com sucesso!");
    }
  };

  const handleSuggestionSelect = (suggestion: any) => {
    setPrescriptionItems([
      ...prescriptionItems,
      {
        ...suggestion,
        item_date: format(new Date(), "yyyy-MM-dd"),
      },
    ]);
  };

  const handleStepChange = async (step: number) => {
    if (step === 3 && prescriptionItems.length === 0) {
      toast.error("Adicione pelo menos um item à receita antes de finalizar");
      return;
    }
    if (step === 3 && !currentPatientId) {
      toast.error("Vincule um paciente antes de finalizar");
      return;
    }

    // Se indo para Step 3 e ainda não salvou a prescrição, salvar agora
    if (step === 3 && !prescriptionId && selectedPatient) {
      try {
        await savePrescriptionMutation.mutateAsync("draft");
      } catch (error) {
        toast.error("Erro ao salvar prescrição");
        return;
      }
    }

    setCurrentStep(step);
  };

  const handleFinalize = async () => {
    if (!currentPatientId || prescriptionItems.length === 0) {
      toast.error("Paciente e itens são obrigatórios");
      return;
    }

    await savePrescriptionMutation.mutateAsync("draft");
    await generatePlanMutation.mutateAsync();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/app/aplicativo/prescription")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Nova Prescrição</h1>
          <p className="text-muted-foreground">
            Captura multimodal com identificação automática
          </p>
        </div>
      </div>

      {/* Horizontal Navigation */}
      <PrescriptionStepsNav
        currentStep={currentStep}
        onStepChange={handleStepChange}
        patientId={currentPatientId}
        hasItems={prescriptionItems.length > 0}
      />

      {/* Step Content */}
      {currentStep === 1 && (
        <div className="space-y-6">
          {/* PRIMEIRO: Captura Multimodal */}
          <MultimodalCapture
            patientId={currentPatientId || ""}
            onItemsExtracted={handleCaptureComplete}
          />

          {/* RODAPÉ: Botão de navegação */}
          <div className="flex justify-end items-center pt-4 border-t">
            <Button onClick={() => setCurrentStep(2)}>
              Ir para Receita
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div className="space-y-6">
          {/* SEÇÃO 1: Dados do Paciente */}
          <section>
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-3 text-teal-700">
              <User className="h-5 w-5" />
              Dados do Paciente
            </h2>
            {selectedPatient ? (
              <div className="space-y-4">
                <PatientPrescriptionSummary patientId={selectedPatient.id} />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedPatient(null);
                      setCurrentPatientId(null);
                    }}
                  >
                    Trocar Paciente
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/app/aplicativo/smartfile/patients/new")}
                  >
                    Criar Novo Paciente
                  </Button>
                </div>
              </div>
            ) : (
              <PrescriptionPatientLinker
                suggestedName={suggestedPatientInfo.name}
                suggestedCPF={suggestedPatientInfo.cpf}
                confidence={suggestedPatientInfo.confidence}
                onPatientLinked={handlePatientLinked}
                currentPatientId={currentPatientId}
              />
            )}
          </section>

          {/* SEÇÃO 2: Receita */}
          <section>
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-3 text-teal-700">
              <FileText className="h-5 w-5" />
              Receita
            </h2>
            <PrescriptionTable
              items={prescriptionItems}
              onItemsChange={setPrescriptionItems}
            />
          </section>

          {/* SEÇÃO 3: Sugestões Personalizadas */}
          <section>
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-3 text-cyan-600">
              <Sparkles className="h-5 w-5" />
              Sugestões Personalizadas
            </h2>
            {selectedPatient ? (
              <PrescriptionSuggestions
                patientId={selectedPatient.id}
                onSelectSuggestion={handleSuggestionSelect}
              />
            ) : (
              <Card className="border-gray-200 bg-gray-50/50">
                <CardContent className="py-6 text-center text-muted-foreground">
                  <Sparkles className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm">Vincule um paciente para ver sugestões personalizadas.</p>
                </CardContent>
              </Card>
            )}
          </section>

          {/* RODAPÉ: Botões */}
          <div className="flex justify-between items-center pt-4 border-t">
            <Button variant="outline" onClick={() => setCurrentStep(1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>

            <Button
              onClick={() => handleStepChange(3)}
              disabled={prescriptionItems.length === 0}
            >
              Finalizar
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {currentStep === 3 && selectedPatient && (
        <PrescriptionFinalizationStep
          prescriptionId={prescriptionId || ""}
          patient={{
            id: selectedPatient.id,
            full_name: selectedPatient.full_name,
            data_nascimento: selectedPatient.data_nascimento,
            cpf: selectedPatient.cpf,
            email: selectedPatient.email,
            celular: selectedPatient.celular,
          }}
          prescriptionItems={prescriptionItems.map((item) => ({
            id: item.id || "",
            prescription: item.name,
            dosage: item.dosage,
            duration: item.duration,
            item_type: item.item_type,
            frequency: item.frequency,
            usage_route: item.usage_route,
          }))}
          professionalName={profile?.full_name || ""}
          professionalCpfCrmCrn={profile?.cpf}
          prescriptionDate={new Date().toISOString()}
        />
      )}
    </div>
  );
}
